import React, { useState, useEffect } from 'react';
import { Typography, Popover, Box, MenuItem, Select, FormControl, Button } from '@mui/material';
import InputField from './InputField';

interface TimeFieldProps {
  label: string;
  name: string;
  value: string | null;
  onChange: (value: string | null) => void;
  isDisabled?: boolean;
  minutesStep?: number;
  showSeconds?: boolean;
  isToday?: boolean;
  isMandatory?: boolean;
}

const TimeField: React.FC<TimeFieldProps> = ({
  label,
  value,
  onChange,
  isDisabled = false,
  minutesStep = 5,
  showSeconds = false,
  isToday,
  isMandatory = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Hours options
  const hours12 = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const hours24 = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  const minutes = Array.from({ length: 60 / minutesStep }, (_, i) =>
    (i * minutesStep).toString().padStart(2, '0'),
  );
  const seconds = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
  const meridiems = ['AM', 'PM'];

  const [selectedHour, setSelectedHour] = useState('12');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [selectedSecond, setSelectedSecond] = useState('00');
  const [selectedMeridiem, setSelectedMeridiem] = useState('AM');

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentSecond = now.getSeconds();

  useEffect(() => {
    if (!value) return;

    if (showSeconds) {
      // Expecting 24-hour with seconds: HH:mm:ss, e.g. "20:30:45"
      const regex24 = /^(\d{2}):(\d{2}):(\d{2})$/;
      const match = value.match(regex24);
      if (match) {
        setSelectedHour(match[1]);
        setSelectedMinute(match[2]);
        setSelectedSecond(match[3]);
        // No meridiem in 24h format
        setSelectedMeridiem('AM');
      }
    } else {
      // Expecting 12-hour without seconds: HH:mm AM/PM, e.g. "08:30 PM"
      const regex12 = /^(\d{2}):(\d{2}) (AM|PM)$/;
      const match = value.match(regex12);
      if (match) {
        setSelectedHour(match[1]);
        setSelectedMinute(match[2]);
        setSelectedMeridiem(match[3]);
        setSelectedSecond('00');
      }
    }
  }, [value, showSeconds]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isDisabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSave = () => {
    let finalTime = '';
    if (showSeconds) {
      // 24-hour with seconds, no AM/PM
      finalTime = `${selectedHour}:${selectedMinute}:${selectedSecond}`;
    } else {
      // 12-hour format with AM/PM
      finalTime = `${selectedHour}:${selectedMinute} ${selectedMeridiem}`;
    }
    onChange(finalTime);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const isSameHour = parseInt(selectedHour, 10) === currentHour;

  return (
    <div className="flex flex-col w-full">
      <Typography variant="body2" fontWeight={600} color="textPrimary">
        {label}
        {isMandatory && <span style={{ color: 'red' }}> *</span>}
      </Typography>

      <div onClick={handleOpen}>
        <InputField
          value={value || ''}
          onChange={() => {}}
          placeholder={showSeconds ? 'Select Time (24h)' : 'Select Time (12h)'}
          isDisabled={isDisabled}
          extraClass="cursor-pointer"
        />
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 2,
            minWidth: 220,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth>
              <Select value={selectedHour} onChange={(e) => setSelectedHour(e.target.value)} size="small">
                {(showSeconds ? hours24 : hours12).map((h) => {
                  const hourNum = parseInt(h, 10);
                  const isDisabled = isToday && hourNum < currentHour;
                  return (
                    <MenuItem key={h} value={h} disabled={isDisabled}>
                      {h}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Typography variant="body2" sx={{ alignSelf: 'center' }}>
              :
            </Typography>

            <FormControl fullWidth>
              <Select value={selectedMinute} onChange={(e) => setSelectedMinute(e.target.value)} size="small">
                {minutes.map((m) => {
                  const minuteNum = parseInt(m, 10);
                  const isDisabled = isToday && isSameHour && minuteNum < currentMinute;
                  return (
                    <MenuItem key={m} value={m} disabled={isDisabled}>
                      {m}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {showSeconds && (
              <>
                <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                  :
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedSecond}
                    onChange={(e) => setSelectedSecond(e.target.value)}
                    size="small"
                  >
                    {seconds.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {!showSeconds && (
              <FormControl fullWidth>
                <Select
                  value={selectedMeridiem}
                  onChange={(e) => setSelectedMeridiem(e.target.value)}
                  size="small"
                >
                  {meridiems.map((mer) => (
                    <MenuItem key={mer} value={mer}>
                      {mer}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <Button variant="contained" onClick={handleSave} size="small">
            Set Time
          </Button>
        </Box>
      </Popover>
    </div>
  );
};

export default TimeField;
